import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ContexteUtilisateur } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { InviterUtilisateursModel } from 'src/models/pilotage/inviterUtilisateursModel';
import { ResultInviterUtilisateursModel } from 'src/models/pilotage/resultInviterUtilisateursModel';
import { InviterAdminsPilotesModel } from 'src/models/pilotage/inviterAdminsPilotesModel';
import { UtilisateurPilotageModel } from 'src/models/pilotage/utilisateurPilotageModel';
import { ModifierUtilisateurPilotageModel } from 'src/models/pilotage/modifierUtilisateurPilotageModel';
import { ActiverUtilisateursModel } from 'src/models/pilotage/activerUtilisateursModel';
import { DesactiverUtilisateursModel } from 'src/models/pilotage/desactiverUtilisateursModel';
import { CreerUtilisateurFictifModel } from 'src/models/pilotage/creerUtilisateurFictifModel';
import { ResultCreerUtilisateurFictifModel } from 'src/models/pilotage/resultCreerUtilisateurFictifModel';
import { ForcerMotDePasseModel } from 'src/models/pilotage/forcerMotDePasseModel';
import { ResultForcerMotDePasseModel } from 'src/models/pilotage/resultForcerMotDePasseModel';
import { GestionEtiquettesUtilisateursModel } from 'src/models/pilotage/gestionEtiquettesUtilisateursModel';
import { DeplacerUtilisateurRootModel } from 'src/models/pilotage/deplacerUtilisateurRootModel';
import { EntiteUtilisateurModel, SelectionEntiteUtilisateurModel } from 'src/models/selectionEntiteUtilisateurModel';
import { AuthService } from './auth.service';
import { UtilisateurModel } from 'src/models/utilisateurModel';
import { TranslateService } from '@ngx-translate/core';
import { ServiceNovoStudioModel } from 'src/models/pilotage/serviceNovoStudioModel';
import { CreerUtilisateurTenantSSOModel } from 'src/models/pilotage/creerUtilisateurTenantSSOModel';
import { ResultCreerUtilisateursTenantSSOModel } from 'src/models/pilotage/resultCreerUtilisateurTenantSSOModel';
import { CreerAdminPiloteTenantSSOModel } from 'src/models/pilotage/creerAdminPiloteTenantSSOModel';
import { StatutCCAQModel } from 'src/models/pilotage/statutCCAQModel';
import { InvitationModel } from 'src/models/invitationModel';
import { EProgressionPaquet } from 'src/enum/eProgressionPaquet';

@Injectable({
  providedIn: 'root'
})
export class UtilisateurService {
  private static readonly ENTITE_SELECTION_STORAGE_KEY = 'lms-entite-utilisateur-selectionnee';
  private baseUrl = environment.api_LMS + 'Utilisateur';
  public entiteSelectionChange: ReplaySubject<EntiteUtilisateurModel> = new ReplaySubject<EntiteUtilisateurModel>();
  public contexteUtilisateur!: ContexteUtilisateur;
  public isModeRoot!: boolean;
  public selectionEntiteUtilisateur!: SelectionEntiteUtilisateurModel;
  private _entiteUtilisateurSelectionnee?: EntiteUtilisateurModel;

  get entiteUtilisateurSelectionnee(): EntiteUtilisateurModel {
    return this._entiteUtilisateurSelectionnee as EntiteUtilisateurModel;
  }

  set entiteUtilisateurSelectionnee(entite: EntiteUtilisateurModel) {
    this._entiteUtilisateurSelectionnee = entite;
    this.persisterEntiteSelectionnee(entite);
  }

  constructor(private http: HttpClient, private authService: AuthService, private translateService: TranslateService) {
    this.restaurerEntiteSelectionnee();
  }

  private restaurerEntiteSelectionnee(): void {
    try {
      const stored = sessionStorage.getItem(UtilisateurService.ENTITE_SELECTION_STORAGE_KEY);
      if (stored) {
        this._entiteUtilisateurSelectionnee = JSON.parse(stored);
      }
    } catch {
      this._entiteUtilisateurSelectionnee = undefined;
    }
  }

  private persisterEntiteSelectionnee(entite?: EntiteUtilisateurModel): void {
    try {
      if (entite) {
        sessionStorage.setItem(UtilisateurService.ENTITE_SELECTION_STORAGE_KEY, JSON.stringify(entite));
      } else {
        sessionStorage.removeItem(UtilisateurService.ENTITE_SELECTION_STORAGE_KEY);
      }
    } catch {
    }
  }
  obtenirContexte(): Observable<ContexteUtilisateur> {
    return this.http.get<ContexteUtilisateur>(`${this.baseUrl}/ObtenirContexte`, { params: { entiteIdPilote: this.entiteUtilisateurSelectionnee.entiteId, utilisateurIdPilote: this.entiteUtilisateurSelectionnee.utilisateurId } })
      .pipe(
        map((contexte: ContexteUtilisateur) => {
          if (contexte.toutesLesEtiquettes) {
            const currentLang = this.translateService.currentLang || 'fr';
            contexte.toutesLesEtiquettes.sort((a, b) => {
              const nomA = (a.locales[currentLang] || Object.values(a.locales)[0] || '').toLowerCase();
              const nomB = (b.locales[currentLang] || Object.values(b.locales)[0] || '').toLowerCase();
              return nomA.localeCompare(nomB);
            });
          }
          return contexte;
        })
      );
  }

  obtenirUtilisateurs(utilisateurId: number, champRecherche?: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/ObtenirUtilisateurs`, { params: { entiteIdPilote: this.entiteUtilisateurSelectionnee.entiteId, utilisateurIdPilote: utilisateurId, champRecherche: champRecherche || '' } });
  }

  obtenirUtilisateurPilotage(utilisateurId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/ObtenirUtilisateurPilotage`, { params: { entiteIdPilote: this.entiteUtilisateurSelectionnee.entiteId, utilisateurId: utilisateurId, utilisateurIdPilote: this.entiteUtilisateurSelectionnee.utilisateurId } });
  }

  activerUtilisateurs(model: ActiverUtilisateursModel): Observable<any> {
    return this.http.put(`${this.baseUrl}/ActiverUtilisateurs`, model);
  }

  desactiverUtilisateurs(model: DesactiverUtilisateursModel): Observable<any> {
    return this.http.put(`${this.baseUrl}/DesactiverUtilisateurs`, model);
  }

  renvoyerInvitationsUtilisateurs(utilisateursId: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/RenvoyerInvitationsUtilisateurs`, utilisateursId);
  }

  inviterEtActiverUtilisateurExistant(utilisateurId: number, entiteId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/InviterEtActiverUtilisateurExistant`, { utilisateurId, entiteId });
  }

  forcerMotDePasse(model: ForcerMotDePasseModel): Observable<ResultForcerMotDePasseModel> {
    return this.http.post<ResultForcerMotDePasseModel>(`${this.baseUrl}/ForcerMotDePasse`, model);
  }

  obtenirModeleUtilisateurFictif(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/ObtenirModeleUtilisateurFictif`, {
      responseType: 'blob'
    });
  }

  obtenirModeleUtilisateurTenantSSO(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/ObtenirModeleUtilisateurTenantSSO`, {
      responseType: 'blob'
    });
  }

  creerUtilisateursFictifs(creerUtilisateursFictifs: CreerUtilisateurFictifModel): Observable<ResultCreerUtilisateurFictifModel[]> {
    return this.http.post<ResultCreerUtilisateurFictifModel[]>(`${this.baseUrl}/CreerUtilisateursFictifs`, creerUtilisateursFictifs);
  }

  obtenirDenominationRole(roleId: number): string {
    var role = this.contexteUtilisateur.roles.find(role => role.roleId == roleId)!;
    return this.getNomLocalise(role.denominations);
  }

  obtenirNomEntite(entiteId: number): string {
    return this.contexteUtilisateur.toutesLesEntites.find(entite => entite.entiteId == entiteId)?.nom!;
  }

  inviterUtilisateurs(inviterUtilisateurs: InviterUtilisateursModel): Observable<ResultInviterUtilisateursModel> {
    return this.http.post<ResultInviterUtilisateursModel>(`${this.baseUrl}/InviterUtilisateurs`, inviterUtilisateurs);
  }

  inviterAdminsPilotes(inviterAdminsPilotes: InviterAdminsPilotesModel): Observable<ResultInviterUtilisateursModel> {
    return this.http.post<ResultInviterUtilisateursModel>(`${this.baseUrl}/InviterAdminsPilotes`, inviterAdminsPilotes);
  }

  creerUtilisateursTenantSSO(creerUtilisateursTenantSSO: CreerUtilisateurTenantSSOModel): Observable<ResultCreerUtilisateursTenantSSOModel> {
    return this.http.post<ResultCreerUtilisateursTenantSSOModel>(`${this.baseUrl}/CreerUtilisateursTenantSSO`, creerUtilisateursTenantSSO);
  }

  creerAdminsPilotesTenantSSO(creerAdminsPilotesTenantSSO: CreerAdminPiloteTenantSSOModel): Observable<ResultCreerUtilisateursTenantSSOModel> {
    return this.http.post<ResultCreerUtilisateursTenantSSOModel>(`${this.baseUrl}/CreerAdminsPilotesTenantSSO`, creerAdminsPilotesTenantSSO);
  }

  modifierUtilisateur(model: ModifierUtilisateurPilotageModel): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/ModifierUtilisateur`, model);
  }

  ajouterUtilisateursEtiquettes(model: GestionEtiquettesUtilisateursModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/AjouterUtilisateursEtiquettes`, model);
  }

  supprimerUtilisateursEtiquettes(model: GestionEtiquettesUtilisateursModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/SupprimerUtilisateursEtiquettes`, model);
  }

  deplacerUtilisateurRoot(model: DeplacerUtilisateurRootModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/DeplacerUtilisateurRoot`, model);
  }

  obtenirUtilisateur(accesRoot: boolean): Observable<any> {
    return this.http.get(`${this.baseUrl}/ObtenirUtilisateur`, { params: { ssoId: this.authService.User?.profile.sub ?? '', accesRoot: accesRoot.toString() } });
  }

  ObtenirInvitation(utilisateurId: number): Observable<InvitationModel> {
    return this.http.get<InvitationModel>(`${this.baseUrl}/ObtenirInvitation`, { params: { utilisateurId : utilisateurId } });
  }

  notifyentiteSelectionChange(entite: EntiteUtilisateurModel) {
    this.entiteSelectionChange.next(entite);
  }

  estRolePiloteOuAdmin(): boolean {
    return this.selectionEntiteUtilisateur.entiteUtilisateurs
      .some(d => d.roleEntites.some(e => e.roleId == 1 || e.roleId == 2));
  }

  exporterUtilisateurs(utilisateurIds: number[], codeLangue: string): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/Exporter`, utilisateurIds, {
      responseType: 'blob',
      params: { codeLangue }
    });
  }

  exporterUtilisateursPdf(utilisateurIds: number[], codeLangue: string): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/ExporterPdf`, utilisateurIds, {
      responseType: 'blob',
      params: { codeLangue }
    });
  }

  obtenirUtilisateurParId(utilisateurId: number): Observable<UtilisateurModel> {
    return this.http.get<UtilisateurModel>(`${this.baseUrl}/ObtenirUtilisateurParId`, {
      params: { utilisateurId }
    });
  }

  obtenirServicesEntiteEtAccesDiffusion(novoEntiteId: number): Observable<ServiceNovoStudioModel> {
    return this.http.get<ServiceNovoStudioModel>(`${this.baseUrl}/ObtenirServicesEntiteEtAccesDiffusion`, {
      params: { novoEntiteId: novoEntiteId.toString() }
    });
  }

  convertirCompteEnCompteFictif(ssoId: string): Observable<ResultForcerMotDePasseModel> {
    return this.http.post<ResultForcerMotDePasseModel>(`${this.baseUrl}/ConvertirCompteEnCompteFictif`, null, {
      params: { ssoId }
    });
  }

  obtenirStatutCCAQ(ssoId: string): Observable<StatutCCAQModel> {
    return this.http.get<StatutCCAQModel>(`${this.baseUrl}/ObtenirStatutCCAQ`, { params: { ssoId } });
  }


  ConvertirComptesEnComptesFictifsAdmin(courriels: string[], motDePasse: string): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/ConvertirComptesEnComptesFictifsAdmin`, {
      courriels: courriels,
      motDePasse: motDePasse
    });
  }

  forcerMotDePasseParEmail(email: string, motDePasse: string): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/ForcerMotDePasseParEmail`, {
      email: email,
      motDePasse: motDePasse
    });
  }

  deplacerUtilisateurs(courriels: string[], novoEntiteIdDestination: number): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/DeplacerUtilisateurs`, {
      courriels: courriels,
      novoEntiteIdDestination: novoEntiteIdDestination
    });
  }

  mettreAJourNumerosEmploye(utilisateurs: any[], novoEntiteId: number): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/MettreAJourNumerosEmploye`, {
      utilisateurs: utilisateurs,
      novoEntiteId: novoEntiteId
    });
  }

    remplacerCourriel(ancienCourriel: string, nouveauCourriel: string): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/RemplacerCourriel`, {
       ancienCourriel: ancienCourriel,
       nouveauCourriel: nouveauCourriel
    });
  }

  changerPrenomNom(identifiant: string , nouveauPrenom: string, nouveauNom: string): Observable<void> {
    return this.http.post<void>(`${environment.api_LMS}Administration/MiseAJourPrenomNom`, {
       adresseCourriel: identifiant,
       prenom: nouveauPrenom,
       nom: nouveauNom
    });
  }

  getNomLocalise(noms: Map<string, string>): string {
    const currentLang = this.translateService.currentLang ? this.translateService.currentLang : 'fr';
    const nomsMap = new Map(Object.entries(noms));

    if (nomsMap.has(currentLang)) return nomsMap.get(currentLang)!;

    return '';
  }

  obtenirLangueCourante(): string {
    return this.translateService.currentLang || 'fr';
  }
}
